import React from 'react';
import { motion } from 'framer-motion';
import { Cloud, Shield, Lock, Server, Network, Database, Key, RefreshCw, CheckCircle, ArrowRight } from 'lucide-react';

interface CloudSecurityProps {
  setCurrentPage: (page: string) => void;
}

const CloudSecurity: React.FC<CloudSecurityProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Shield,
      title: "Cloud Security Assessment",
      description: "Comprehensive evaluation of your cloud infrastructure security posture.",
      items: [
        "Security gap analysis",
        "Risk assessment",
        "Compliance evaluation",
        "Architecture review"
      ]
    },
    {
      icon: Lock,
      title: "Access Management",
      description: "Robust identity and access management solutions for cloud resources.",
      items: [
        "Identity verification",
        "Access control",
        "Privilege management",
        "Session monitoring"
      ]
    },
    {
      icon: Server,
      title: "Infrastructure Protection",
      description: "Advanced security measures for cloud servers and infrastructure.",
      items: [
        "Network security",
        "Endpoint protection",
        "Container security",
        "Workload protection"
      ]
    },
    {
      icon: Database,
      title: "Data Security",
      description: "Comprehensive protection for sensitive data in the cloud.",
      items: [
        "Data encryption",
        "Access controls",
        "DLP solutions",
        "Backup security"
      ]
    }
  ];

  const services = [
    {
      icon: Network,
      title: "Multi-Cloud Security",
      description: "Unified security management across multiple cloud platforms.",
      items: [
        "AWS security",
        "Azure protection",
        "Google Cloud security",
        "Hybrid cloud security"
      ]
    },
    {
      icon: Key,
      title: "Zero Trust Architecture",
      description: "Implementation of zero trust principles for enhanced security.",
      items: [
        "Identity verification",
        "Least privilege access",
        "Network segmentation",
        "Continuous monitoring"
      ]
    },
    {
      icon: RefreshCw,
      title: "Continuous Monitoring",
      description: "24/7 monitoring and real-time threat detection for cloud infrastructure.",
      items: [
        "Real-time monitoring",
        "Threat detection",
        "Incident response",
        "Performance analysis"
      ]
    }
  ];

  const capabilities = [
    {
      icon: Shield,
      title: "Security Controls",
      items: [
        "Access management",
        "Data protection",
        "Network security",
        "Application security"
      ]
    },
    {
      icon: Database,
      title: "Data Protection",
      items: [
        "Encryption at rest",
        "Encryption in transit",
        "Key management",
        "Data governance"
      ]
    },
    {
      icon: Network,
      title: "Network Security",
      items: [
        "Firewall management",
        "Traffic monitoring",
        "DDoS protection",
        "VPN security"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block mb-6"
          >
            <Cloud className="h-16 w-16 text-primary animate-glow" />
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold mb-6"
          >
            <span className="text-gradient">Cloud Security Solutions</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg text-muted max-w-3xl mx-auto"
          >
            Comprehensive cloud security solutions to protect your infrastructure, applications, and data in the cloud
          </motion.p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="glass-panel rounded-xl p-8 hover-card"
            >
              <div className="flex items-center mb-6">
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-2xl font-semibold ml-4">{feature.title}</h3>
              </div>
              
              <p className="text-muted mb-6">{feature.description}</p>
              
              <ul className="space-y-3">
                {feature.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                    <span className="text-muted">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Services Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Cloud Security Services</h2>
            <p className="text-lg text-muted">
              Comprehensive security services for your cloud environment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <service.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{service.title}</h3>
                </div>
                
                <p className="text-muted mb-6">{service.description}</p>

                <ul className="space-y-3">
                  {service.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Capabilities Section */}
        <div className="mb-20">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Security Capabilities</h2>
            <p className="text-lg text-muted">
              Advanced security capabilities for cloud environments
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {capabilities.map((capability, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-lg">
                    <capability.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">{capability.title}</h3>
                </div>

                <ul className="space-y-3">
                  {capability.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <h2 className="text-3xl font-bold mb-6">Ready to Secure Your Cloud Infrastructure?</h2>
            <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
              Get started with our cloud security services and protect your business in the cloud
            </p>
            <button 
              onClick={() => setCurrentPage('get-started')}
              className="btn-primary"
            >
              <span className="flex items-center justify-center">
                Request Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </span>
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default CloudSecurity;